import type { FC } from 'react'

import { Icon, Skeleton } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useQuery } from '@tanstack/react-query'
import CheckIcon from 'mdi-react/CheckIcon'

import { Translation } from '@/app/translations/Translation'
import { CustomerFeatureStatus } from '@/modules/admin/resources/features/customerFeatureStatusEnum'

import adminQueries from '../../queries'
import type { Feature } from '../../resources'
import adminResources from '../../resources'

export const CustomerSelection: FC<{
  selectedCustomerId: number | null
  customerFeatures: Feature['customerFeatures'] | undefined
  isDataLoading: boolean
  isSelectedCustomerActivationHistory: boolean
  onSetSelectedCustomerId: (id: number) => void
  onClose: () => void
  onContinue: () => void
  onSeeActivationHistory: () => void
}> = ({
  selectedCustomerId,
  customerFeatures,
  isDataLoading,
  onContinue,
  onSetSelectedCustomerId,
  onClose,
}) => {
  const { data: customers, isLoading } = useQuery(
    adminQueries.adminCustomers(),
    adminResources.getCustomers
  )

  if (isLoading || isDataLoading)
    return (
      <div className="grid grid-cols-3 gap-2">
        <Skeleton height="80px" width={300} />
        <Skeleton height="80px" width={300} />
        <Skeleton height="80px" width={300} />
      </div>
    )

  return (
    <div>
      <div className="grid grid-cols-3 gap-2">
        {customers?.map(customer => {
          const isActivated =
            customerFeatures?.find(customerInfo => customerInfo.customerId === customer.id)
              ?.statusId === CustomerFeatureStatus.Active

          const isRequested =
            customerFeatures?.find(customerInfo => customerInfo.customerId === customer.id)
              ?.statusId === CustomerFeatureStatus.Requested

          const isSelected = selectedCustomerId === customer.id
          return (
            <Button
              variant={isSelected ? 'primary' : 'secondary'}
              key={customer.id}
              className="h-20"
              onClick={() => onSetSelectedCustomerId(customer.id)}
            >
              <div className="flex flex-col items-center">
                <div>{customer.name}</div>
                {isActivated && (
                  <div className="mt-0.5 inline-flex items-center">
                    <Icon
                      isWhiteLabeled={false}
                      size="xs"
                      colorMode={isSelected ? 'white' : 'success'}
                      className="mr-1 inline-flex"
                    >
                      <CheckIcon />
                    </Icon>
                    <Translation className="text-xs lowercase" id="active" />
                  </div>
                )}
                {isRequested && (
                  <div className="mt-0.5 inline-flex items-center">
                    <Translation className="text-xs lowercase" id="requested" />
                  </div>
                )}
              </div>
            </Button>
          )
        })}
      </div>

      <div className="mt-3 flex justify-end">
        <Button variant="tertiary" onClick={onClose}>
          <Translation id="Cancel" />
        </Button>

        <Button variant="primary" disabled={!selectedCustomerId} onClick={onContinue}>
          <Translation id="See terms & conditions" />
        </Button>
      </div>
    </div>
  )
}
